 
import React, {
  FC, useState, useEffect, useCallback,
} from 'react';
import AUButtonComponent from '@aarhus-university/au-lib-react-components/src/components/AUButtonComponent';
import { useTranslationsContext } from '../../lib/translation';
import { useSendIdentityVerificationCodeMutation, useVerifyIdentityCodeMutation, useResolveAsDoubleIdentityMutation } from '../../services/api';

type Props = {
  fuzzyMatchCaseId: string;
  obfuscatedCandidates: IObfuscatedCandidate[];
  allowDoubleIdentities?: boolean;
}

type VerificationState = 'initial' | 'codeSent' | 'verified' | 'maxAttemptsExceeded' | 'confirmDoubleIdentity' | 'doubleIdentityCreated';

const IdentityVerificationComponent: FC<Props> = ({
  fuzzyMatchCaseId,
  obfuscatedCandidates,
  allowDoubleIdentities = false,
}) => {
  const translations = useTranslationsContext();
  const [selectedCandidateAuid, setSelectedCandidateAuid] = useState<number | null>(null);
  const [code, setCode] = useState('');
  const [verificationState, setVerificationState] = useState<VerificationState>('initial');
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [sendCode, sendCodeResponse] = useSendIdentityVerificationCodeMutation();
  const [verifyCode, verifyCodeResponse] = useVerifyIdentityCodeMutation();
  const [resolveAsDoubleIdentity, resolveDoubleIdentityResponse] = useResolveAsDoubleIdentityMutation();

  const sendBusy = sendCodeResponse.isLoading;
  const verifyBusy = verifyCodeResponse.isLoading;
  const doubleIdentityBusy = resolveDoubleIdentityResponse.isLoading;

  // Nedtællingstimer
  useEffect(() => {
    if (remainingSeconds <= 0) return undefined;
    const timer = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [remainingSeconds]);

  const formatTime = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleSendCode = useCallback(async () => {
    if (!selectedCandidateAuid) return;
    setErrorMessage(null);
    setCode('');
    try {
      const result = await sendCode({
        fuzzyMatchCaseId,
        candidateAuid: selectedCandidateAuid,
      }).unwrap();

      if (result.success) {
        setVerificationState('codeSent');
        const expiresInSeconds = result.codeExpiresAt
          ? Math.round((Date.parse(result.codeExpiresAt) - Date.now()) / 1000)
          : NaN;
        setRemainingSeconds(Number.isFinite(expiresInSeconds) ? Math.max(0, expiresInSeconds) : 900);
      } else {
        setErrorMessage(result.message);
      }
    } catch {
      setErrorMessage(translations.errorHeader);
    }
  }, [selectedCandidateAuid, fuzzyMatchCaseId, sendCode, translations.errorHeader]);

  const handleVerifyCode = useCallback(async () => {
    if (!selectedCandidateAuid || !code) return;
    setErrorMessage(null);
    try {
      const result = await verifyCode({
        fuzzyMatchCaseId,
        candidateAuid: selectedCandidateAuid,
        code,
      }).unwrap();

      if (result.verified && result.success) {
        setVerificationState('verified');
      } else if (result.verified) {
        // Identity confirmed, but the account could not be processed — the case stays in the admin queue
        setErrorMessage(translations.errorHeader);
      } else if (result.maxAttemptsExceeded) {
        setVerificationState('maxAttemptsExceeded');
      } else if (result.codeExpired) {
        setErrorMessage(translations.identityCodeExpired);
        setRemainingSeconds(0);
      } else {
        const msg = result.remainingAttempts != null
          ? `${translations.identityWrongCode} ${result.remainingAttempts} ${translations.identityAttemptsLeft}`
          : translations.identityWrongCode;
        setErrorMessage(msg);
      }
    } catch {
      setErrorMessage(translations.errorHeader);
    }
  }, [selectedCandidateAuid, code, fuzzyMatchCaseId, verifyCode, translations]);

  const handleConfirmDoubleIdentity = useCallback(async () => {
    setErrorMessage(null);
    try {
      const result = await resolveAsDoubleIdentity({ fuzzyMatchCaseId }).unwrap();
      if (result.success) {
        setVerificationState('doubleIdentityCreated');
      } else {
        setErrorMessage(result.message);
      }
    } catch {
      setErrorMessage(translations.errorHeader);
    }
  }, [fuzzyMatchCaseId, resolveAsDoubleIdentity, translations.errorHeader]);

  // Verified — success-besked
  if (verificationState === 'verified') {
    return (
      <div className="page__content__block">
        <div className="receipt">
          <div className="receipt__icon" data-icon="" aria-hidden="true" />
          <h2 className="receipt__header">{translations.identityVerified}</h2>
          <p className="text--large">{translations.identityVerifiedText}</p>
        </div>
      </div>
    );
  }

  // Max attempts — sagen er i admin-køen
  if (verificationState === 'maxAttemptsExceeded') {
    return (
      <div className="page__content__block">
        <div className="receipt">
          <div className="receipt__icon" data-icon="" aria-hidden="true" />
          <h2 className="receipt__header">{translations.identityMaxAttemptsReached}</h2>
          <p className="text--large">{translations.identityWillBeContacted}</p>
        </div>
      </div>
    );
  }

  // Confirm double identity — user is about to bypass verification
  if (verificationState === 'confirmDoubleIdentity') {
    return (
      <div className="page__content__block">
        <h1>{translations.doubleIdentityConfirmHeading}</h1>
        <p className="text--large">{translations.doubleIdentityConfirmText}</p>
        {errorMessage && (
          <div className="form-info--error">
            <span className="form-info__message">{errorMessage}</span>
          </div>
        )}
        <div className="button-container vertical-spacing-top">
          <AUButtonComponent
            label={translations.doubleIdentityConfirmButton}
            disabled={doubleIdentityBusy}
            mode={doubleIdentityBusy ? 'processing' : 'none'}
            onClick={handleConfirmDoubleIdentity}
          />
          <AUButtonComponent
            label={translations.doubleIdentityCancelButton}
            disabled={doubleIdentityBusy}
            onClick={() => setVerificationState('initial')}
          />
        </div>
      </div>
    );
  }

  // Double identity created — success receipt
  if (verificationState === 'doubleIdentityCreated') {
    return (
      <div className="page__content__block">
        <div className="receipt">
          <div className="receipt__icon" data-icon="" aria-hidden="true" />
          <h2 className="receipt__header">{translations.doubleIdentityCreatedHeading}</h2>
          <p className="text--large">{translations.doubleIdentityCreatedText}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page__content__block">
      <h1>{translations.identityVerificationHeading}</h1>
      <p className="text--large">{translations.identityVerificationText}</p>

      {verificationState === 'initial' && (
        <>
          <form className="form">
            <fieldset>
              {obfuscatedCandidates.map((candidate) => (
                <div className="form__field form__field--horizontal" key={candidate.candidateAuid}>
                  <label htmlFor={`candidate-${candidate.candidateAuid}`}>
                    {` ${candidate.obfuscatedEmail}`}
                  </label>
                  <input
                    type="radio"
                    id={`candidate-${candidate.candidateAuid}`}
                    name="candidateSelect"
                    value={candidate.candidateAuid}
                    checked={selectedCandidateAuid === candidate.candidateAuid}
                    onChange={() => setSelectedCandidateAuid(candidate.candidateAuid)}
                  />
                </div>
              ))}
            </fieldset>
          </form>
          <p className="text--small text--secondary">{translations.identitySelectCandidateHelp}</p>
          {errorMessage && (
            <div className="form-info--error">
              <span className="form-info__message">{errorMessage}</span>
            </div>
          )}
          <div className="button-container vertical-spacing-top">
            <AUButtonComponent
              label={translations.identitySendCode}
              disabled={!selectedCandidateAuid || sendBusy}
              mode={sendBusy ? 'processing' : 'none'}
              onClick={handleSendCode}
            />
          </div>
          {allowDoubleIdentities && (
            <div className="button-container vertical-spacing-top">
              <AUButtonComponent
                label={translations.identityNoneOfTheseAreMe}
                onClick={() => setVerificationState('confirmDoubleIdentity')}
              />
            </div>
          )}
          <p className="text--small vertical-spacing-top" dangerouslySetInnerHTML={{ __html: translations.identityNeedHelp }} />
        </>
      )}

      {verificationState === 'codeSent' && (
        <>
          <p>{translations.identityCodeSent}</p>
          {remainingSeconds > 0 && (
            <p className="text--small">{`${translations.identityCodeExpiresIn} ${formatTime(remainingSeconds)}`}</p>
          )}
          <form className="form">
            <div className="form__field">
              <label htmlFor="verificationCode">{translations.identityEnterCode}</label>
              <input
                type="text"
                id="verificationCode"
                maxLength={6}
                pattern="[0-9]*"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                disabled={verifyBusy}
              />
            </div>
          </form>
          {errorMessage && (
            <div className="form-info--error">
              <span className="form-info__message">{errorMessage}</span>
            </div>
          )}
          <div className="button-container vertical-spacing-top">
            <AUButtonComponent
              label={translations.identityVerifyCode}
              disabled={code.length !== 6 || verifyBusy}
              mode={verifyBusy ? 'processing' : 'none'}
              onClick={handleVerifyCode}
            />
            <AUButtonComponent
              label={translations.identityResendCode}
              disabled={sendBusy}
              mode={sendBusy ? 'processing' : 'none'}
              onClick={handleSendCode}
            />
          </div>
          {allowDoubleIdentities && (
            <div className="button-container vertical-spacing-top">
              <AUButtonComponent
                label={translations.identityNoneOfTheseAreMe}
                onClick={() => setVerificationState('confirmDoubleIdentity')}
              />
            </div>
          )}
          <p className="text--small vertical-spacing-top" dangerouslySetInnerHTML={{ __html: translations.identityNeedHelp }} />
        </>
      )}
    </div>
  );
};

IdentityVerificationComponent.displayName = 'IdentityVerificationComponent';
export default IdentityVerificationComponent;
